import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Search,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "early", label: "Early" },
  { value: "on time", label: "On Time" },
  { value: "delayed", label: "Delayed" },
  { value: "pending", label: "Pending" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

function safeDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

export default function ShipmentPerformanceDetail() {
  const nav = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const initialStatus = params.get("status") || "all";
  const initialCustomer = params.get("customer") || "";
  const fromDate = params.get("from") || "";
  const toDate = params.get("to") || "";
  const initialScope = params.get("scope") || "po";

  const [status, setStatus] = useState(initialStatus);
  const [customer, setCustomer] = useState(initialCustomer);
  const [scope] = useState(initialScope);

  const [rows, setRows] = useState([]);
  const [customerRows, setCustomerRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const [sort, setSort] = useState({
    field: "",
    direction: "asc",
  });

  const isCustomerSummary = scope === "customers";
  const isOnWaterLifecycle = scope === "on-water";

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        if (isCustomerSummary) {
          const response = await api.post(
            "/ai/ceo-chat/shipment-performance",
            {
              from_date: fromDate || undefined,
              to_date: toDate || undefined,
            },
            { timeout: 35000 }
          );

          const data = response?.data ?? response;

          if (!cancelled) {
            setCustomerRows(
              Array.isArray(data?.customer_rows) ? data.customer_rows : []
            );
            setSummary(data?.summary || null);
            setRows([]);
          }

          return;
        }

        if (isOnWaterLifecycle) {
          const response = await api.post(
            "/ai/ceo-chat/shipment-performance/on-water-detail",
            {
              from_date: fromDate || undefined,
              to_date: toDate || undefined,
              customer_name: customer || undefined,
            },
            { timeout: 35000 }
          );

          const data = response?.data ?? response;

          if (!cancelled) {
            setRows(Array.isArray(data?.rows) ? data.rows : []);
            setCustomerRows([]);
          }

          return;
        }

        const response = await api.post(
          "/ai/ceo-chat/shipment-performance/detail",
          {
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            customer_name: customer || undefined,
            promptness_status: status || "all",
          },
          { timeout: 35000 }
        );

        const data = response?.data ?? response;

        if (!cancelled) {
          setRows(Array.isArray(data?.rows) ? data.rows : []);
          setCustomerRows([]);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setCustomerRows([]);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Shipment performance details could not be loaded safely."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, customer, status, isCustomerSummary, isOnWaterLifecycle]);

  useEffect(() => {
    setPage(1);
  }, [search, status, customer, pageSize, sort]);

  const activeRows = isCustomerSummary ? customerRows : rows;

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return activeRows;

    return activeRows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [activeRows, search]);

  const sortedRows = useMemo(() => {
    if (!sort.field) return searchedRows;

    const copy = [...searchedRows];

    copy.sort((a, b) => {
      const av = a?.[sort.field];
      const bv = b?.[sort.field];

      const aNum = Number(av);
      const bNum = Number(bv);
      const bothNumeric =
        av !== "" &&
        bv !== "" &&
        av !== null &&
        bv !== null &&
        !Number.isNaN(aNum) &&
        !Number.isNaN(bNum);

      let result = 0;

      if (bothNumeric) {
        result = aNum - bNum;
      } else {
        result = String(av ?? "").localeCompare(String(bv ?? ""));
      }

      return sort.direction === "desc" ? -result : result;
    });

    return copy;
  }, [searchedRows, sort]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedRows.length / pageSize)
  );

  const currentPage = Math.min(page, totalPages);

  const pagedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const changeSort = (field) => {
    setSort((current) => {
      if (current.field === field) {
        return {
          field,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        field,
        direction: "asc",
      };
    });
  };

  const updateUrl = ({ nextStatus = status, nextCustomer = customer }) => {
    const next = new URLSearchParams();

    next.set("scope", scope);

    if (nextStatus) next.set("status", nextStatus);
    if (nextCustomer) next.set("customer", nextCustomer);
    if (fromDate) next.set("from", fromDate);
    if (toDate) next.set("to", toDate);

    nav(
      `/ceo-command-center/shipment-performance?${next.toString()}`,
      { replace: true }
    );
  };

  const applyStatus = (value) => {
    setStatus(value);
    updateUrl({ nextStatus: value });
  };

  const clearCustomer = () => {
    setCustomer("");
    updateUrl({ nextCustomer: "" });
  };

  const exportCsv = () => {
    if (!sortedRows.length) return;

    const exportColumns = isCustomerSummary
      ? [
          "CustomerName",
          "TotalPOs",
          "EarlyCount",
          "OnTimeCount",
          "DelayedCount",
          "PendingShipment",
          "OnOrBeforeRatePct",
        ]
      : isOnWaterLifecycle
        ? [
            "PoNo",
            "PoDate",
            "CustomerName",
            "BuyerName",
            "DestinationCountry",
            "CInvNo",
            "TransportedDate",
            "ShippedOn",
            "ETA",
            "DeliveredDate",
            "BLNumber",
            "ContainerNo",
          ]
        : [
            "PoNo",
            "PoDate",
            "ShipByDate",
            "StockOutDate",
            "CustomerName",
            "BuyerName",
            "DestinationCountry",
            "PromptnessStatus",
            "VarianceDays",
          ];

    const csv = [
      exportColumns.map(csvEscape).join(","),
      ...sortedRows.map((row) =>
        exportColumns.map((column) => csvEscape(row?.[column])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const suffix = isCustomerSummary
      ? "customers"
      : customer
        ? customer.replace(/[^a-z0-9]+/gi, "-").toLowerCase()
        : status.replace(/\s+/g, "-");

    a.href = url;
    a.download = `shipment-performance-${suffix || "details"}.csv`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const poColumns = [
    ["PoNo", "PO Number"],
    ["PoDate", "PO Date"],
    ["ShipByDate", "Ship By"],
    ["StockOutDate", "Stock Out"],
    ["CustomerName", "Customer / Consignee"],
    ["BuyerName", "Buyer"],
    ["DestinationCountry", "Destination"],
    ["PromptnessStatus", "Status"],
    ["VarianceDays", "Variance Days"],
  ];

  const onWaterColumns = [
    ["PoNo", "PO Number"],
    ["PoDate", "PO Date"],
    ["CustomerName", "Customer / Consignee"],
    ["BuyerName", "Buyer"],
    ["DestinationCountry", "Destination"],
    ["CInvNo", "Commercial Invoice"],
    ["TransportedDate", "Transported Date"],
    ["ShippedOn", "Shipped On"],
    ["ETA", "ETA"],
    ["DeliveredDate", "Delivered Date"],
    ["BLNumber", "BL Number"],
    ["ContainerNo", "Container Number"],
  ];

  const customerColumns = [
    ["CustomerName", "Customer / Buyer"],
    ["TotalPOs", "Total POs"],
    ["EarlyCount", "Early"],
    ["OnTimeCount", "On Time"],
    ["DelayedCount", "Delayed"],
    ["PendingShipment", "Pending"],
    ["OnOrBeforeRatePct", "On / Before %"],
  ];

  const columns = isCustomerSummary
    ? customerColumns
    : isOnWaterLifecycle
      ? onWaterColumns
      : poColumns;

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <button
              type="button"
              className="btn btn-ghost mb-2"
              onClick={() =>
                nav("/ceo-command-center?tab=shipment")
              }
            >
              <ArrowLeft size={14} strokeWidth={1.8} />
              Back to Dispatch
            </button>

            <h1>
              {isCustomerSummary
                ? "Customer / Buyer Shipment Performance"
                : isOnWaterLifecycle
                  ? "On-water Shipment Lifecycle"
                  : "PO Shipment Performance"}
            </h1>

            <p>
              {isCustomerSummary
                ? "Customer-level shipment promptness across the selected ShipByDate period."
                : isOnWaterLifecycle
                  ? "PO-level shipments currently On-water: TransportedDate present and DeliveredDate blank/null."
                  : customer
                    ? `PO-level shipment performance for ${customer}.`
                    : `PO-level shipment performance across all customers${status !== "all" ? ` · ${status}` : ""}.`}
            </p>
          </div>

          <button
            type="button"
            className="erp-primary-btn"
            disabled={!sortedRows.length}
            onClick={exportCsv}
          >
            <Download size={14} strokeWidth={1.8} />
            Download CSV
          </button>
        </div>

        <div className="space-y-4">
          <div className="card-flat p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[260px]">
                <label className="label">Search table</label>
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    style={{ left: "12px" }}
                  />
                  <input
                    className="input"
                    style={{ paddingLeft: "38px" }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      isCustomerSummary
                        ? "Search customer..."
                        : isOnWaterLifecycle
                          ? "Search PO, customer, invoice, BL, container, destination..."
                          : "Search PO, customer, buyer, destination, status..."
                    }
                  />
                </div>
              </div>

              {!isCustomerSummary && !isOnWaterLifecycle && (
                <div>
                  <label className="label">Promptness</label>
                  <select
                    className="select"
                    value={status}
                    onChange={(e) => applyStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!isCustomerSummary && customer && (
                <div>
                  <label className="label">Customer</label>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={clearCustomer}
                  >
                    {customer} ×
                  </button>
                </div>
              )}

              <div>
                <label className="label">Rows per page</label>
                <select
                  className="select"
                  value={pageSize}
                  onChange={(e) =>
                    setPageSize(Number(e.target.value))
                  }
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-4 text-[12px] text-slate-500">
              <span>
                Period: {fromDate || "2026-04-01"} → {toDate || "2027-03-31"}
              </span>
              <span>{sortedRows.length.toLocaleString()} matching record(s)</span>
              {!isCustomerSummary && (
                <span>
                  Date basis: {isOnWaterLifecycle
                    ? "ScrmShipmentDetailsBreif.TransportedDate"
                    : "ScrmPoData.ShipByDate"}
                </span>
              )}
            </div>
          </div>

          {isCustomerSummary && summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                ["Eligible POs", summary.eligible_pos],
                ["Shipped / Classified", summary.classified_shipped_pos],
                ["Pending", summary.pending_shipments],
                ["Early", summary.early_count],
                ["Delayed", summary.delayed_count],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="card-flat p-4"
                >
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">
                    {label}
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {Number(value || 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="card-flat overflow-hidden">
            {loading ? (
              <div className="p-8 text-sm text-slate-500">
                Loading shipment performance...
              </div>
            ) : error ? (
              <div className="m-4 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                {error}
              </div>
            ) : (
              <>
                <div className="overflow-auto">
                  <table className="tbl w-full">
                    <thead>
                      <tr>
                        {columns.map(([field, label]) => (
                          <th
                            key={field}
                            onClick={() => changeSort(field)}
                            className="cursor-pointer whitespace-nowrap"
                            title={`Sort by ${label}`}
                          >
                            <span className="inline-flex items-center gap-1">
                              {label}
                              {sort.field === field &&
                                (sort.direction === "asc" ? (
                                  <ChevronUp size={12} />
                                ) : (
                                  <ChevronDown size={12} />
                                ))}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {pagedRows.map((row, index) => (
                        <tr
                          key={
                            isCustomerSummary
                              ? `${row?.CustomerName || "row"}-${index}`
                              : `${row?.PoID || row?.PoNo || "row"}-${index}`
                          }
                          className={
                            isCustomerSummary
                              ? "cursor-pointer"
                              : ""
                          }
                          onClick={
                            isCustomerSummary
                              ? () => {
                                  const next = new URLSearchParams();
                                  next.set("scope", "po");
                                  next.set("status", "all");
                                  next.set(
                                    "customer",
                                    row?.CustomerName || "Unassigned"
                                  );
                                  if (fromDate) next.set("from", fromDate);
                                  if (toDate) next.set("to", toDate);

                                  nav(
                                    `/ceo-command-center/shipment-performance?${next.toString()}`
                                  );
                                }
                              : undefined
                          }
                        >
                          {columns.map(([field]) => (
                            <td
                              key={field}
                              className={
                                [
                                  "TotalPOs",
                                  "EarlyCount",
                                  "OnTimeCount",
                                  "DelayedCount",
                                  "PendingShipment",
                                  "OnOrBeforeRatePct",
                                  "VarianceDays",
                                ].includes(field)
                                  ? "numeric whitespace-nowrap"
                                  : "whitespace-nowrap"
                              }
                            >
                              {field === "PoDate" ||
                              field === "ShipByDate" ||
                              field === "StockOutDate" ||
                              field === "TransportedDate" ||
                              field === "ShippedOn" ||
                              field === "ETA" ||
                              field === "DeliveredDate"
                                ? safeDate(row?.[field])
                                : field === "OnOrBeforeRatePct"
                                  ? row?.[field] === null ||
                                    row?.[field] === undefined
                                    ? "—"
                                    : `${Number(row[field]).toFixed(2)}%`
                                  : row?.[field] === null ||
                                    row?.[field] === undefined ||
                                    row?.[field] === ""
                                    ? "—"
                                    : row[field]}
                            </td>
                          ))}
                        </tr>
                      ))}

                      {!pagedRows.length && (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="text-center py-10 text-slate-500"
                          >
                            No shipment performance records match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-slate-500">
                    Page {currentPage.toLocaleString()} of{" "}
                    {totalPages.toLocaleString()} ·{" "}
                    {sortedRows.length.toLocaleString()} record(s)
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={currentPage <= 1}
                      onClick={() => setPage(1)}
                    >
                      First
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={currentPage <= 1}
                      onClick={() =>
                        setPage((p) => Math.max(1, p - 1))
                      }
                    >
                      Previous
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={currentPage >= totalPages}
                      onClick={() =>
                        setPage((p) =>
                          Math.min(totalPages, p + 1)
                        )
                      }
                    >
                      Next
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      Last
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="card-flat p-4 text-[12px] leading-5 text-slate-500">
            {isOnWaterLifecycle
              ? "On-water = TransportedDate present and DeliveredDate blank/null. Period basis is TransportedDate. PO status codes 2, 4 and 7 are excluded."
              : "Early = StockOutDate before ShipByDate · On Time = same date · Delayed = StockOutDate after ShipByDate · Pending = StockOutDate blank/null. TransportedDate is not used as a promptness fallback."}
          </div>
        </div>
      </main>
    </div>
  );
}
